import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import css from './SelectDate.scss'
import moment from 'moment';
import PropTypes from 'prop-types';
import { padTwoChars } from '../../utils/intake';
import { isNull } from 'lodash';

const defaultDateFormat = 'YYYY-MM-DD'
const dayOptions = [...Array(31).keys()].map((i) => ({ label: padTwoChars(i + 1), value: padTwoChars(i + 1) }));
const monthOptions = [
    { label: "January", value: "01" },
    { label: "February", value: "02" },
    { label: "March", value: "03" },
    { label: "April", value: "04" },
    { label: "May", value: "05" },
    { label: "June", value: "06" },
    { label: "July", value: "07" },
    { label: "August", value: "08" },
    { label: "September", value: "09" },
    { label: "October", value: "10" },
    { label: "November", value: "11" },
    { label: "December", value: "12" },
];
const getYearsOptions = () => {
    const numberOfOptions = 130;
    const startRange = new Date().getFullYear() - 90;
    const options = [...Array(numberOfOptions).keys()].map((i) =>
        ({
            label: `${startRange + numberOfOptions - i}`,
            value: `${startRange + numberOfOptions - i}`
        })
    )

    return options;
}
const yearsOptions = getYearsOptions();

const formatDateValue = (format, value) => {
    const date = moment(value, defaultDateFormat);
    return date.isValid() ? date.format(format) : null
}
const getCurrentDay = () => moment().format('DD')
const selectTheme = theme => ({
    ...theme,
    colors: {
        ...theme.colors,
        primary: '#1E70A6',
        neutral20: '#57595B'
    },
})

const isDateNotEmpty = (day, month, year) => (!isNull(day) && !isNull(month) && !isNull(year));
const isDatePartiallyFilled = (day, month, year, monthAndYearOnly) => (!(isNull(day) || monthAndYearOnly) || !isNull(month) || !isNull(year));

const SelectDate = (props) => {
    const { value, hasError, onDateChange } = props
    /* In case of monthAndYearOnly: legacy api expect complete date similar to Full date.
     * so, for Month and year field, setting current day as default day. But still day field will not be displayed.  */
    const [day, setDay] = useState(props.monthAndYearOnly ? getCurrentDay() : formatDateValue('DD', value))
    const [month, setMonth] = useState(formatDateValue('MM', value))
    const [year, setYear] = useState(formatDateValue('YYYY', value))
    const [isAlreadyEdited, setIsAlreadyEdited] = useState(false)
    const relativeElementIds = [`select-date-day-${props.id}`, `select-date-month-${props.id}`, `select-date-year-${props.id}`]

    useEffect(() => {
        if (isDateNotEmpty(day, month, year)) {
            const date = moment(`${year}-${month}-${day}`)
            onDateChange(date.format(defaultDateFormat), date.isValid())
        } else if (isDatePartiallyFilled(day, month, year, props.monthAndYearOnly)) {
            // Partialy filled date(Not on initial select) will be considered as invalid and value set as 'Invalid Date'
            onDateChange('Invalid Date', !isAlreadyEdited ? true : false)
        } else {
            // If Date is completely empty
            onDateChange(null, true)
        }
    }, [day, month, year, isAlreadyEdited]);

    const onChange = (field, option) => {
        if (field === 'DAY') {
            setDay(option === null ? null : option.value)
        } else if (field === 'MONTH') {
            setMonth(option === null ? null : option.value)
        } else if (field === 'YEAR') {
            setYear(option === null ? null : option.value)
        }
    }

    const onBlur = (event) => {
        //In case of IE11 focusing out of single select will be considered as Already Edited
        //On Other browsers focusing out of all date selects will be considered as Already Edited
        if (isNull(event.relatedTarget) || (event.relatedTarget && !relativeElementIds.includes(event.relatedTarget.id))) {
            setIsAlreadyEdited(true);
        }
    }

    const errorClassName = (hasError ? css['error'] : '');
    return (
        <div className={`${css['datePickerWraper']} ${errorClassName}`} role="application">
            <Select
                aria-label="Select the Month"
                className={css['monthSelectComponent']}
                classNamePrefix={'select-date'}
                options={monthOptions}
                placeholder={'Month'}
                isClearable
                theme={selectTheme}
                components={{ ClearIndicator: () => null, IndicatorSeparator: () => null }}
                value={monthOptions.filter((option) => option.value === month)}
                onChange={(option) => onChange('MONTH', option)}
                inputId={`select-date-month-${props.id}`}
                onBlur={onBlur}
            />
            {!props.monthAndYearOnly &&
                <Select
                    aria-label="Select the Day"
                    className={css['selectComponent']}
                    classNamePrefix={'select-date'}
                    options={dayOptions}
                    placeholder={'Day'}
                    isClearable
                    components={{ ClearIndicator: () => null, IndicatorSeparator: () => null }}
                    theme={selectTheme}
                    value={dayOptions.filter((option) => option.value === day)}
                    onChange={(option) => onChange('DAY', option)}
                    inputId={`select-date-day-${props.id}`}
                    onBlur={onBlur}
                />
            }
            <Select
                aria-label="Select the Year"
                className={css['selectComponent']}
                classNamePrefix={'select-date'}
                options={yearsOptions}
                placeholder={'Year'}
                isClearable
                theme={selectTheme}
                components={{ ClearIndicator: () => null, IndicatorSeparator: () => null }}
                value={yearsOptions.filter((option) => option.value === year)}
                onChange={(option) => onChange('YEAR', option)}
                inputId={`select-date-year-${props.id}`}
                onBlur={onBlur}
            />
        </div>
    )
}

SelectDate.propTypes = {
    value: PropTypes.string,
    hasError: PropTypes.bool,
    onDateChange: PropTypes.func,
    id: PropTypes.number.isRequired,
    monthAndYearOnly: PropTypes.bool
}

export default SelectDate;
